import { HttpClient } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { forkJoin, from, Observable } from 'rxjs';
import { map, shareReplay, switchMap } from 'rxjs/operators';
import { importLibrary, setOptions } from '@googlemaps/js-api-loader';
import { environment } from 'src/environments/environment';
import { APIConstants } from '../service.constant';
import { IGeocoderResult } from '@interfaces';

@Injectable({
  providedIn: 'root',
})
export class GoogleMapsService {
  private readonly http = inject(HttpClient);

  private googleMapsInit$!: Observable<boolean>;

  /**
   * Loads the Google Maps and Marker libraries once using the API key
   */
  public loadGoogleMapsScript(): Observable<boolean> {
    if (!this.googleMapsInit$) {
      this.googleMapsInit$ = this.fetchGoogleMapsApiKey().pipe(
        switchMap((apiKey) => {
          setOptions({
            key: apiKey,
          });
          const maps$ = from(importLibrary('maps'));
          const marker$ = from(importLibrary('marker'));

          return forkJoin([maps$, marker$]);
        }),
        map(() => true),
        shareReplay(1) // Cache the result for future calls
      );
    }

    return this.googleMapsInit$;
  }

  /**
   * Retrieves the Google Maps API key from the backend
   */
  private fetchGoogleMapsApiKey(): Observable<string> {
    const url = `${environment.apiGatewayBaseURL}${APIConstants.GET_GOOGLE_KEY}`;
    const query = `?subscription-key=${environment.ocpApimSubscriptionKey}&resourceType=web`;

    return this.http
      .get<{ key: string }>(`${url}${query}`)
      .pipe(map((res) => res.key));
  }

  /**
   * Gets geocoded coordinates from an address string
   */
  public getCoordinates(address: string): Observable<IGeocoderResult[]> {
    const url = `${environment.apiGatewayBaseURL}${APIConstants.GET_COORDINATES}`;
    const query = `?subscription-key=${environment.ocpApimSubscriptionKey
      }&address=${encodeURIComponent(address)}`;

    return this.http.get<IGeocoderResult[]>(`${url}${query}`);
  }
}
